import type { CompareProduct } from './compareProvider';

const STORAGE_PREFIX = 'jane_compare_selection:';

export const compareStorageKey = (storeId: number | string) =>
  `${STORAGE_PREFIX}${storeId}`;

type Check = (value: unknown) => boolean;

const isString: Check = (value) => typeof value === 'string';
const isNumber: Check = (value) => typeof value === 'number';
const isStringArray: Check = (value) =>
  Array.isArray(value) && value.every(isString);
const nullable =
  (check: Check): Check =>
  (value) =>
    value === null || check(value);
const optional =
  (check: Check): Check =>
  (value) =>
    value === undefined || check(value);
const anything: Check = () => true;

const FIELD_CHECKS: Record<keyof CompareProduct, Check> = {
  activities: isStringArray,
  aggregateRating: nullable(isNumber),
  availableWeights: isStringArray,
  brand: nullable(isString),
  category: nullable(isString),
  dosage: nullable(isString),
  feelings: isStringArray,
  flavors: isStringArray,
  goldTagLabel: optional(nullable(isString)),
  id: isNumber,
  imageUrl: isString,
  kind: isString,
  name: isString,
  originalPrice: nullable(isString),
  // The owning app validates this with its own guard.
  payload: anything,
  percentCbd: nullable(isNumber),
  percentThc: nullable(isNumber),
  price: nullable(isString),
  reviewCount: nullable(isNumber),
  specialLabel: optional(nullable(isString)),
  url: optional(nullable(isString)),
};

const isCompareProduct = (value: unknown): value is CompareProduct => {
  if (!value || typeof value !== 'object') return false;
  const product = value as Record<string, unknown>;
  return Object.entries(FIELD_CHECKS).every(([key, check]) =>
    check(product[key])
  );
};

export const readCompareSelection = (key: string): CompareProduct[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isCompareProduct);
  } catch {
    return [];
  }
};

export const clearOtherCompareSelections = (keepKey: string): void => {
  if (typeof window === 'undefined') return;
  try {
    Object.keys(window.sessionStorage)
      .filter((key) => key.startsWith(STORAGE_PREFIX) && key !== keepKey)
      .forEach((key) => window.sessionStorage.removeItem(key));
  } catch {
    // Storage unavailable — nothing was written there to begin with.
  }
};

export const writeCompareSelection = (
  key: string,
  products: CompareProduct[]
): void => {
  if (typeof window === 'undefined') return;
  try {
    if (products.length === 0) {
      window.sessionStorage.removeItem(key);
      return;
    }
    window.sessionStorage.setItem(key, JSON.stringify(products));
  } catch {
    // Storage unavailable or full — the comparison won't persist after reload.
  }
};
